/**
 * «Хозяйство ▾» — ЕДИНСТВЕННЫЙ переключатель области в приложении (AK-895,
 * решение founder В-3 от 08.10). На экране всегда ровно ОДИН его экземпляр —
 * в шапке (AK-974, часть 2; approve founder 20.10, комменты 18132/18140/18141):
 * десктоп ≥768 — пилюля `pill` правее «Сезон ▾», как на проде v1.2010
 * (c184ceb07e, `DashboardScopeSelector`); мобайл <768 — `header`. Сайдбар
 * переключатель не рендерит.
 *
 * Правило 28.08 «панель = умолчание, локальный выбор = отклонение» снято
 * (свидетельство о смерти — `specs/2026-10-07-farm-centric-ui/02-questions.md`, В-3).
 *
 * Поведение:
 *   · шторка на штатном `ScopeTreeView`: алфавит + поиск, группы по кластерам
 *     с гектарами; кластер — заголовок группы, НЕ выбирается (90% работы —
 *     внутри одного хозяйства, выбор «кластера целиком» в этой модели нет);
 *   · первая строка «Холдинг — <имя>» — только если `/scope/available` отдал
 *     company-строку. Бэк отдаёт её строго по данным: членство без кластера и
 *     хозяйства (роли уровня компании, в т.ч. мониторинг техники `@company`).
 *     Роли кластера/хозяйства её не получают — и строки у них нет. Решать это
 *     по списку ролей на фронте нельзя: второй источник правды разъедется;
 *   · выбор пишет `POST /scope/set` через `useScopeSetter` (один путь записи);
 *   · выбирать не из чего (одно хозяйство, без «Холдинга») — имя без ▾ и без
 *     шторки: мёртвый контрол хуже отсутствующего.
 *
 * Варианты кнопки (`variant`):
 *   · `rail`   — свёрнутый рельс 72px: квадрат 40×40 с глифом и тултипом;
 *   · `full`   — развёрнутое меню (мобильная шторка меню): «Хозяйство / имя ▾»;
 *   · `header` — мобильная шапка: компактная кнопка, шторка выезжает сверху;
 *   · `pill`   — десктопная шапка, вид прод-пилюли v1.2010: глиф области, имя,
 *                пашня, ▾; шторка справа 380px; уровни как на проде —
 *                «Холдинг» (компания) / кластер / хозяйство.
 *   · `rail` / `full` — сейчас нигде не рендерятся (сайдбар без переключателя,
 *                AK-974); оставлены, чтобы не множить диф оболочки.
 */
import { useMemo, useState } from 'react';
import { AlertCircle, Building2, ChevronDown, MapPin, Network, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ErrorState } from '@/components/ErrorState';
import { classifyApiError } from '@/lib/apiError';
import { cn } from '@/lib/utils';
import {
  ScopeIcon,
  ScopeTreeView,
  fmtScopeArea,
  scopeArableHa,
  topLevelScopeName,
  type ScopeInfo,
  type ScopeSelection,
} from './ScopeTree';
import { rememberLastScope } from './lastScope';
import { useAvailableScopes } from './useAvailableScopes';
import { useScopeSetter, useSyncScopeCurrent } from './useScopeSetter';

/** Тот же стандарт глифа, что у всего рельса (`Sidebar` NAV_ICON): lucide 24, stroke 2, рендер 20. */
const RAIL_ICON = { size: 20, strokeWidth: 2 } as const;

export type FarmSwitcherVariant = 'rail' | 'full' | 'header' | 'pill';

export interface FarmSwitcherProps {
  variant: FarmSwitcherVariant;
  /**
   * Имя компании — запасная подпись «Холдинга», когда в `/scope/available`
   * нет holding-строки, а company-строка подписана плейсхолдером
   * «Вся компания» (так бэк подписывает её всегда).
   */
  companyName?: string | null;
  className?: string;
}

type Mode = 'holding' | 'cluster' | 'farm';

function modeOf(s: ScopeInfo): Mode {
  if (s.scope_type === 'farm' || s.scope_type === 'brigade') return 'farm';
  if (s.scope_type === 'cluster') return 'cluster';
  return 'holding';
}

const MODE_CAPTION: Record<Mode, string> = {
  holding: 'Холдинг',
  cluster: 'Кластер',
  farm: 'Хозяйство',
};

function ModeIcon({ mode }: { mode: Mode }) {
  if (mode === 'holding') return <Building2 {...RAIL_ICON} />;
  if (mode === 'cluster') return <Network {...RAIL_ICON} />;
  return <MapPin {...RAIL_ICON} />;
}

const byName = (a: ScopeInfo, b: ScopeInfo) => a.name.localeCompare(b.name, 'ru');

export function FarmSwitcher({ variant, companyName, className }: FarmSwitcherProps) {
  const scopes = useAvailableScopes();
  const setter = useScopeSetter('FarmSwitcher.setScope');
  useSyncScopeCurrent();
  const [open, setOpen] = useState(false);

  // Переключатель живёт в сайдбаре КАЖДОГО экрана: исключение здесь роняет
  // навигацию целиком. Ответ без списка (заглушка, чужой формат) — значит
  // «переключать нечего», а не повод уронить меню.
  const raw = scopes.data;
  const data = raw && Array.isArray(raw.available) && raw.current ? raw : undefined;

  // Алфавит — на нашей стороне: порядок бэка зависит от collation БД, а
  // правило выпадашек «алфавит + поиск» сквозное. Кластеры сортируем тоже —
  // это порядок групп.
  const available = useMemo(() => {
    if (!data) return [] as ScopeInfo[];
    const farms = data.available.filter((s) => s.scope_type === 'farm').sort(byName);
    const clusters = data.available.filter((s) => s.scope_type === 'cluster').sort(byName);
    const rest = data.available.filter(
      (s) => s.scope_type !== 'farm' && s.scope_type !== 'cluster',
    );
    return [...rest, ...clusters, ...farms];
  }, [data]);

  if (!data) {
    if (raw) return null;
    if (scopes.isError) {
      const message = classifyApiError(scopes.error, 'FarmSwitcher.scopeAvailable').message;
      if (variant === 'rail') {
        return (
          <div className={className}>
            <Tooltip delayDuration={150}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => void scopes.refetch()}
                  aria-label="Не удалось загрузить хозяйства. Повторить"
                  className="mx-auto my-1 flex h-10 w-10 items-center justify-center rounded-md text-destructive transition-colors hover:bg-accent"
                >
                  <AlertCircle {...RAIL_ICON} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{message}</TooltipContent>
            </Tooltip>
          </div>
        );
      }
      return (
        <div className={className}>
          <ErrorState
            variant="inline"
            message="Не удалось загрузить хозяйства"
            onRetry={() => void scopes.refetch()}
          />
        </div>
      );
    }
    // Пока грузится — место под кнопку держим, чтобы меню не прыгало вниз.
    return (
      <div className={className} aria-hidden>
        <Skeleton
          className={cn(
            variant === 'rail' ? 'mx-auto my-1 h-10 w-10' : 'h-10 w-full',
            variant === 'header' && 'w-24',
            variant === 'pill' && 'h-9 w-40',
          )}
        />
      </div>
    );
  }

  const farms = available.filter((s) => s.scope_type === 'farm');
  const companyRow = available.find((s) => s.scope_type === 'company');
  const holdingName =
    available.find((s) => s.scope_type === 'holding')?.name?.trim() ||
    companyName?.trim() ||
    topLevelScopeName(available);

  const optionsCount = farms.length + (companyRow ? 1 : 0);
  if (optionsCount === 0) return null;
  const switchable = optionsCount > 1;

  const cur = data.current;
  const mode = modeOf(cur);
  // Выбирать не из чего — показываем единственный вариант, а не то, что
  // случайно лежит в сессии (например, кластер у члена кластера с одним
  // хозяйством).
  const shown: { mode: Mode; name: string; scope: ScopeInfo } = !switchable
    ? companyRow
      ? { mode: 'holding', name: holdingName, scope: companyRow }
      : { mode: 'farm', name: farms[0].name, scope: farms[0] }
    : mode === 'holding'
      ? { mode, name: holdingName, scope: companyRow ?? cur }
      : { mode, name: cur.name, scope: cur };
  const caption = MODE_CAPTION[shown.mode];
  const fullLabel = `${caption}: ${shown.name}`;

  const value: ScopeSelection | null = mode === 'holding' ? null : cur;

  const pick = async (s: ScopeSelection) => {
    const ok = await setter.setScope(s);
    if (!ok) return;
    // Выбор человека — его и откроем при следующем входе (AK-896).
    rememberLastScope(s);
    setOpen(false);
  };

  const trigger = renderTrigger({
    variant,
    switchable,
    mode: shown.mode,
    caption,
    name: shown.name,
    fullLabel,
    areaHa: scopeArableHa(shown.scope),
    saving: setter.saving,
    onOpen: () => setOpen(true),
  });

  if (!switchable) return <div className={className}>{trigger}</div>;

  const side = variant === 'header' ? 'top' : variant === 'pill' ? 'right' : 'left';

  return (
    <div className={className}>
      {trigger}
      <Sheet
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setter.clearError();
        }}
      >
        <SheetContent
          side={side}
          hideClose
          className={cn(
            'flex flex-col gap-0 p-0',
            side === 'top'
              ? 'max-h-[85dvh]'
              : side === 'right'
                ? 'w-[380px] sm:max-w-[380px]'
                : 'w-full sm:w-96 sm:max-w-96',
          )}
        >
          <SheetTitle className="sr-only">Выбор хозяйства</SheetTitle>
          <SheetDescription className="sr-only">
            Все разделы меню работают в выбранном хозяйстве
          </SheetDescription>
          <div className="flex items-center justify-end px-4 pt-4">
            <SheetClose asChild>
              <Button variant="ghost" size="icon" aria-label="Закрыть">
                <X className="h-4 w-4" />
              </Button>
            </SheetClose>
          </div>
          {setter.error && (
            <div className="px-4 pb-2">
              <ErrorState variant="inline" message={setter.error} />
            </div>
          )}
          <ScopeTreeView
            available={available}
            value={value}
            onPick={(s) => void pick(s)}
            showCompanyRow={false}
            clustersSelectable={variant === 'pill'}
            headerName="Выберите хозяйство"
            searchPlaceholder="Найти хозяйство…"
            topOption={
              companyRow
                ? {
                    label: `Холдинг — ${holdingName}`,
                    hint:
                      companyRow.farms_count != null
                        ? `${companyRow.farms_count} хоз. · общая аналитика`
                        : 'общая аналитика',
                    active: mode === 'holding',
                    onSelect: () => void pick(companyRow),
                  }
                : undefined
            }
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}

interface TriggerArgs {
  variant: FarmSwitcherVariant;
  switchable: boolean;
  mode: Mode;
  caption: string;
  name: string;
  fullLabel: string;
  areaHa: number | null | undefined;
  saving: boolean;
  onOpen: () => void;
}

function renderTrigger({
  variant,
  switchable,
  mode,
  caption,
  name,
  fullLabel,
  areaHa,
  saving,
  onOpen,
}: TriggerArgs) {
  if (variant === 'pill') {
    // Прод v1.2010 (`DashboardScopeSelector.tsx:148-149`): пилюля шапки.
    // Пашня одним числом (AK-752, решение founder 25.08).
    const pill =
      'inline-flex min-w-0 max-w-[280px] items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm';
    const body = (
      <>
        <ScopeIcon
          type={mode === 'holding' ? 'company' : mode === 'cluster' ? 'cluster' : 'farm'}
          className="h-4 w-4 shrink-0"
        />
        <span className="truncate font-medium" title={name}>
          {name}
        </span>
        {areaHa != null && (
          <span className="shrink-0 text-xs text-muted-foreground">{fmtScopeArea(areaHa)}</span>
        )}
      </>
    );
    if (!switchable) {
      return (
        <div data-testid="farm-switcher-static" className={pill}>
          {body}
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={onOpen}
        disabled={saving}
        aria-label={`${fullLabel}. Сменить`}
        data-testid="farm-switcher-trigger"
        data-variant="pill"
        className={cn(
          pill,
          'transition-colors hover:bg-accent disabled:cursor-wait disabled:opacity-60',
        )}
      >
        {body}
        <ChevronDown className="h-3 w-3 shrink-0 opacity-60" />
      </button>
    );
  }

  if (variant === 'rail') {
    const square =
      'mx-auto my-1 flex h-10 w-10 items-center justify-center rounded-md border border-input text-foreground';
    return (
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>
          {switchable ? (
            <button
              type="button"
              onClick={onOpen}
              disabled={saving}
              aria-label={`${fullLabel}. Сменить`}
              data-testid="farm-switcher-trigger"
              className={cn(square, 'bg-background transition-colors hover:bg-accent')}
            >
              <ModeIcon mode={mode} />
            </button>
          ) : (
            <div
              role="img"
              aria-label={fullLabel}
              data-testid="farm-switcher-static"
              tabIndex={0}
              className={cn(square, 'bg-secondary/40')}
            >
              <ModeIcon mode={mode} />
            </div>
          )}
        </TooltipTrigger>
        <TooltipContent side="right">{fullLabel}</TooltipContent>
      </Tooltip>
    );
  }

  const text = (
    <span className="min-w-0 flex-1 text-left">
      <span className="block text-xs leading-4 text-muted-foreground">{caption}</span>
      <span className="block truncate text-sm font-medium leading-5" title={name}>
        {name}
      </span>
    </span>
  );

  const box = cn(
    'flex min-w-0 items-center gap-2 rounded-md border border-input',
    variant === 'header' ? 'max-w-40 px-2 py-1' : 'w-full px-3 py-1',
  );

  if (!switchable) {
    return (
      <div data-testid="farm-switcher-static" className={cn(box, 'bg-secondary/40')}>
        {text}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={saving}
      aria-label={`${fullLabel}. Сменить`}
      data-testid="farm-switcher-trigger"
      className={cn(
        box,
        'bg-background transition-colors hover:bg-accent disabled:cursor-wait disabled:opacity-60',
      )}
    >
      {text}
      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );
}

export default FarmSwitcher;
